import { useEffect } from 'react'

interface SEOProps {
  title: string
  description: string
  canonical?: string
  ogTitle?: string
  ogDescription?: string
  ogType?: string
  ogImage?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

const SITE_ORIGIN = 'https://documentiq.algoscale.com'
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/algoscale-logo.png`

function absolutize(url: string): string {
  if (!url) return DEFAULT_OG_IMAGE
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `${SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`
}

export function useSEO({ title, description, canonical, ogTitle, ogDescription, ogType, ogImage, jsonLd }: SEOProps) {
  useEffect(() => {
    // Clear any stale page-specific JSON-LD scripts left over from a previous
    // route OR from the build-time prerender snapshot. The hook then re-injects
    // a fresh set below. Without this, prerendered output would carry duplicate
    // schema-org blocks once React hydrates and the effect runs.
    document
      .querySelectorAll('script[data-seo-jsonld]')
      .forEach((el) => el.remove())

    const prev = document.title
    document.title = `${title} | DocumentIQ`

    const setMeta = (attr: string, key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', value)
    }

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', ogTitle || title)
    setMeta('property', 'og:description', ogDescription || description)
    // Twitter cards mirror OG values — many crawlers (Slack, LinkedIn) check both.
    setMeta('name', 'twitter:title', ogTitle || title)
    setMeta('name', 'twitter:description', ogDescription || description)
    if (ogType) setMeta('property', 'og:type', ogType)
    // Per-route OG image — when a page provides one, override the homepage
    // default. Keeps link previews on Twitter/LinkedIn/Slack visually distinct
    // for blog posts, case studies, and solution pages.
    const resolvedImage = absolutize(ogImage ?? DEFAULT_OG_IMAGE)
    setMeta('property', 'og:image', resolvedImage)
    setMeta('name', 'twitter:image', resolvedImage)
    if (canonical) {
      setMeta('property', 'og:url', canonical)
      setMeta('name', 'twitter:url', canonical)
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
      }
      link.setAttribute('href', canonical)
    }

    const scriptEls: HTMLScriptElement[] = []
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd]
      for (const schema of schemas) {
        const el = document.createElement('script')
        el.type = 'application/ld+json'
        el.setAttribute('data-seo-jsonld', '')
        el.textContent = JSON.stringify(schema)
        document.head.appendChild(el)
        scriptEls.push(el)
      }
    }

    // Marker the build-time prerender script (scripts/prerender.mjs) waits on
    // before snapshotting the page. Without it, the snapshot races the effect
    // and the static HTML lands with the default index.html meta tags.
    document.documentElement.setAttribute('data-seo-ready', 'true')

    return () => {
      document.title = prev
      scriptEls.forEach((el) => el.remove())
    }
  }, [title, description, canonical, ogTitle, ogDescription, ogType, ogImage, jsonLd])
}
